import { useState } from 'react';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import { serviceRequestService } from '../../../services/serviceRequestService';

const CallbackForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    serviceType: '',
    urgency: '',
    preferredTime: '',
    message: ''
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const serviceOptions = [
    { value: 'plumbing-emergency', label: 'Plumbing Emergency' },
    { value: 'plumbing-general', label: 'General Plumbing' },
    { value: 'electrical-emergency', label: 'Electrical Emergency' },
    { value: 'electrical-general', label: 'General Electrical' },
    { value: 'commercial', label: 'Commercial Project' },
    { value: 'consultation', label: 'Consultation' }
  ];

  const urgencyOptions = [
    { value: 'emergency', label: 'Emergency (Within 1 hour)' },
    { value: 'urgent', label: 'Urgent (Same day)' },
    { value: 'normal', label: 'Normal (Within 24 hours)' },
    { value: 'flexible', label: 'Flexible (This week)' }
  ];

  const timeOptions = [
    { value: 'morning', label: 'Morning (8AM - 12PM)' },
    { value: 'afternoon', label: 'Afternoon (12PM - 5PM)' },
    { value: 'evening', label: 'Evening (5PM - 8PM)' },
    { value: 'anytime', label: 'Anytime' }
  ];

  const handleChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors?.[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors?.[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.name?.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData?.phone?.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^[0-9]{10}$/?.test(formData?.phone?.replace(/\s/g, ''))) {
      newErrors.phone = 'Please enter a valid 10-digit phone number';
    }

    if (formData?.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(formData?.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData?.serviceType) {
      newErrors.serviceType = 'Please select a service type';
    }

    if (!formData?.urgency) {
      newErrors.urgency = 'Please select urgency level';
    }

    return newErrors;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setSubmitError('');

    const newErrors = validateForm();
    if (Object.keys(newErrors)?.length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsSubmitting(true);

    try {
      const { data, error } = await serviceRequestService?.create(formData);

      if (error) {
        throw error;
      }

      setSubmitSuccess(true);
      setFormData({
        name: '',
        phone: '',
        email: '',
        serviceType: '',
        urgency: '',
        preferredTime: '',
        message: ''
      });

      setTimeout(() => {
        setSubmitSuccess(false);
      }, 5000);
    } catch (error) {
      setSubmitError(error?.message || 'Failed to submit request. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitSuccess) {
    return (
      <div className="bg-success text-success-foreground rounded-lg p-6 md:p-8 text-center">
        <div className="w-16 h-16 md:w-20 md:h-20 bg-success-foreground text-success rounded-full flex items-center justify-center mx-auto mb-4">
          <Icon name="CheckCircle" size={32} className="md:w-10 md:h-10" />
        </div>
        <h3 className="font-headline font-bold text-xl md:text-2xl mb-2">
          Request Received!
        </h3>
        <p className="font-body text-sm md:text-base mb-4">
          We'll call you back shortly at the number provided.
        </p>
        <p className="font-body text-sm opacity-90">
          For immediate assistance, call <span className="font-accent font-semibold">082 793 5785</span>
        </p>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-lg p-6 md:p-8 shadow-sm border border-border">
      <div className="mb-6">
        <h2 className="font-headline font-bold text-xl md:text-2xl lg:text-3xl mb-2">
          Request a Callback
        </h2>
        <p className="font-body text-sm md:text-base text-muted-foreground">
          Fill out the form below and we'll contact you at your preferred time
        </p>
      </div>

      {submitError && (
        <div className="bg-error/10 border border-error text-error rounded-lg p-3 mb-4 text-sm">
          {submitError}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4 md:space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
          <Input
            label="Full Name"
            type="text"
            name="name"
            placeholder="Enter your name"
            value={formData?.name}
            onChange={handleChange}
            error={errors?.name}
            required
          />

          <Input
            label="Phone Number"
            type="tel"
            name="phone"
            placeholder="082 123 4567"
            value={formData?.phone}
            onChange={handleChange}
            error={errors?.phone}
            required
          />
        </div>

        <Input
          label="Email Address (Optional)"
          type="email"
          name="email"
          placeholder="your.email@example.com"
          value={formData?.email}
          onChange={handleChange}
          error={errors?.email}
          description="We'll send a confirmation email"
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
          <Select
            label="Service Type"
            options={serviceOptions}
            value={formData?.serviceType}
            onChange={(value) => handleSelectChange('serviceType', value)}
            error={errors?.serviceType}
            placeholder="Select service"
            required
          />

          <Select
            label="Urgency Level"
            options={urgencyOptions}
            value={formData?.urgency}
            onChange={(value) => handleSelectChange('urgency', value)}
            error={errors?.urgency}
            placeholder="Select urgency"
            required
          />
        </div>

        <Select
          label="Preferred Callback Time"
          options={timeOptions}
          value={formData?.preferredTime}
          onChange={(value) => handleSelectChange('preferredTime', value)}
          placeholder="Select preferred time"
          description="We'll do our best to accommodate your preference"
        />

        <div>
          <label className="block font-body font-medium text-sm md:text-base text-foreground mb-2">
            Additional Details (Optional)
          </label>
          <textarea
            name="message"
            value={formData?.message}
            onChange={handleChange}
            placeholder="Describe your issue or requirements..."
            rows={4}
            className="w-full px-4 py-3 bg-background border border-input rounded-lg font-body text-sm md:text-base text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent transition-all resize-none"
          />
        </div>

        <div className="flex flex-col sm:flex-row gap-3 md:gap-4 pt-2">
          <Button
            type="submit"
            variant="default"
            size="lg"
            loading={isSubmitting}
            className="w-full sm:flex-1"
          >
            {isSubmitting ? 'Submitting...' : 'Request Callback'}
          </Button>

          <Button
            type="button"
            variant="outline"
            size="lg"
            onClick={() => window.location.href = 'tel:0827935785'}
            className="w-full sm:w-auto"
            iconName="Phone"
            iconPosition="left"
          >
            Call Now Instead
          </Button>
        </div>

        <p className="text-xs md:text-sm text-muted-foreground text-center pt-2">
          By submitting this form, you agree to be contacted by meriodine
        </p>
      </form>
    </div>
  );
};

export default CallbackForm;